import { ContactForm, ContactItem, Page, TextHeading } from '@/components';
import { AppLayout } from '@/layouts';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { ReactElement } from 'react';
import { fetchCompany } from '@/services';
import { ICompany } from '@/shared/types';
import { SSR } from './_app';

const PAGE_COVER_BG_URL = '/images/backgrounds/Contact Page_Head.jpg';

const ContactContent = ({ company }: { company: ICompany }) => {
  return (
    <div className="px-12 md:px-20 py-12 md:py-20">
      <TextHeading
        as="h2"
        alignment="top-left"
        className="text-2xl leading-loose font-bold text-black mb-10"
      >
        ช่องทางการติดต่อ
      </TextHeading>
      <div className="flex flex-col gap-5">
        <ContactItem
          icon="fi-rr-map-marker"
          iconClasses="text-primary"
          title="ที่อยู่"
          titleClasses="text-lg"
          subTitleClasses="text-base"
          element={company.address}
        />
        <ContactItem
          icon="fi-rr-phone-call"
          iconClasses="text-primary"
          title="เบอร์ติดต่อ"
          titleClasses="text-lg"
          subTitleClasses="text-base"
          element={
            <p className="whitespace-pre-line text-base">
              สำนักงาน: {company.phone}{' '}
              {company.phone_ext ? `ต่อ ${company.phone_ext}` : null} <br />{' '}
              มือถือ: {company.mobile_phone}
            </p>
          }
        />
        <ContactItem
          icon="fi-brands-facebook"
          iconClasses="text-primary"
          title="Facebook"
          titleClasses="text-lg"
          subTitleClasses="text-base"
          element={
            <a href={process.env.socialFbUrl}>Perfect Hunter Recruitment</a>
          }
        />
        <ContactItem
          icon="fi-brands-line"
          iconClasses="text-primary"
          title="LINE@"
          titleClasses="text-lg"
          subTitleClasses="text-base"
          element={<a href={process.env.socialLineUrl}>Line Official</a>}
        />
        <ContactItem
          icon="fi-rr-envelope"
          iconClasses="text-primary"
          title="อีเมล"
          titleClasses="text-lg"
          subTitleClasses="text-base"
          element={<a href={`mailto:${company.email}`}>{company.email}</a>}
        />
      </div>
    </div>
  );
};

const AddressMap = () => {
  return (
    <div className="google-maps-phr-address-wrapper border-0">
      <iframe
        height="100%"
        id="google-maps-phr-address"
        src="https://maps.google.com/maps?q=perfect-hunter&t=&z=15&ie=UTF8&iwloc=&output=embed"
        frameBorder="0"
        scrolling="no"
        className="w-full overflow-auto"
      ></iframe>
    </div>
  );
};

export const getServerSideProps: GetServerSideProps<{
  company: ICompany;
}> = async (_) => {
  const company = await fetchCompany();

  return {
    props: {
      company,
    },
  };
};

const ContactUs: SSR<typeof getServerSideProps> = ({ company }) => {
  return (
    <Page>
      <Head>
        <title>{`ติดต่อเรา - ${process.env.COMPANY_NAME}`}</title>
      </Head>

      <Page.Header bgUrl={PAGE_COVER_BG_URL}>
        <Page.Heading
          title="ติดต่อเรา"
          subTitle="บริษัทจัดหางาน เพอร์เฟค ฮันเตอร์ จำกัด"
        />
      </Page.Header>

      <main>
        {/* begin: contact list */}
        <section className="px-3 sm:px-0 py-16 lg:py-24 bg-gray-light">
          <div className="container mx-auto bg-white">
            <div className="grid grid-flow-row lg:grid-flow-col grid-cols-1 lg:grid-cols-2 auto-rows-fr lg:auto-cols-fr">
              <ContactContent company={company} />
              <AddressMap />
            </div>
          </div>
        </section>
        {/* end: contact list */}
        {/* begin: contact from */}
        <section className="px-3 sm:px-0 py-16 lg:py-24">
          <div className="container mx-auto bg-gray-light">
            <ContactForm />
          </div>
        </section>
        {/* end: contact from */}
      </main>
    </Page>
  );
};

ContactUs.getLayout = function getLayout(page: ReactElement) {
  return <AppLayout>{page}</AppLayout>;
};

export default ContactUs;
